import React, {useEffect, useState} from "react";
import {Navigate} from "react-router-dom";
import Cookies from 'js-cookie';

import {axiosInstance} from "../utilities/AxiosConfig";
import {ToastContainer} from 'react-toastify';
import {useSpinner} from "../context/SpinnerContext";
import {notify} from "../components/toast";

import {InputIcon} from "primereact/inputicon";
import {IconField} from "primereact/iconfield";
import {InputText} from "primereact/inputtext";
import {Button} from 'primereact/button';
import {DropSelect} from "../components/Select/DropSelect";

export default function Login() {
    const [user, setUser] = useState("");
    const [password, setPassword] = useState("");
    const [login, setLogin] = useState(false);
    const [loginError, setLoginError] = useState(false);
    const [databases, setDatabases] = useState([]);
    const [database, setDatabase] = useState("");
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [isLockedOut, setIsLockedOut] = useState(false);
    const [lockoutTimer, setLockoutTimer] = useState(0);
    const [dbName, setDbName] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const {loading, setLoading} = useSpinner();

    useEffect(() => {
        Cookies.set('name', "");
        Cookies.set('level', 0);
        Cookies.set('database', "");
        Cookies.set('user', "");
    }, []);

    useEffect(() => {
        setLoading(true);
        axiosInstance.get("account/database/all").then((response) => {
            if (response.data.length === 0) {
                notify("No Database Found", "error");
            } else {
                setDatabase(response.data[response.data.length - 1].value);
                setDbName(response.data[response.data.length - 1].name);
                setDatabases(response.data.sort((a, b) => b.id - a.id));
            }
        }).catch((error) => {
            notify(error.message, "error");
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    const MAX_ATTEMPTS = 3;
    const LOCKOUT_DURATION = 1000; // seconds

    useEffect(() => {
        let timer;
        if (isLockedOut && lockoutTimer > 0) {
            timer = setTimeout(() => {
                setLockoutTimer(prev => prev - 1);
            }, 1000);
        } else if (isLockedOut && lockoutTimer === 0) {
            setIsLockedOut(false);
            setFailedAttempts(0);
        }
        return () => clearTimeout(timer);
    }, [isLockedOut, lockoutTimer]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isLockedOut) return;
        validate();
    };

    function handleDatabaseChange(e) {
        const db = databases.find(it => it.name.trim() === e.trim()).value;
        setDbName(e);
        setDatabase(db);
    }

    const validate = () => {
        if (database === "") {
            notify("Please select a database", "error");
        } else {
            if (user === "" || password === "") {
                setLoginError(true);
            } else {
                setLoading(true);
                axiosInstance.get(`user/${user}&${password}&${database}`).then((response) => {
                    const res = response.data;
                    if (response.data !== 0) {
                        setFailedAttempts(0);
                        setIsLockedOut(false);
                        setLogin(true);
                        setLoginError(false);
                        Cookies.set('user', user);
                        Cookies.set('level', res);
                        Cookies.set('name', databases.find(it => it.value.trim() === database).name.trim());
                        Cookies.set('database', database);
                    } else {
                        setFailedAttempts(prev => prev + 1);
                        if (failedAttempts + 1 >= MAX_ATTEMPTS) {
                            setIsLockedOut(true);
                            setLockoutTimer(LOCKOUT_DURATION);
                        }
                        Cookies.set('user', "");
                        Cookies.set('level', "0,0,0,0,0,0,0,0,0,0,0,0,0,0,0");
                        Cookies.set('name', "");
                        Cookies.set('database', "");
                        setLoginError(true);
                        setLogin(false);
                    }
                }).catch((error) => {
                    notify(error.message, "error");
                }).finally(() => {
                    setLoading(false);
                });
            }
        }
    };

    if (login) return <Navigate to="/home"/>;

    // --- Modern corporate styles (inline, so you don’t need a new CSS file) ---
    const styles = {
        page: {
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "32px 16px",
            background:
                "radial-gradient(1200px 600px at 15% 10%, rgba(59,130,246,.20), transparent 60%)," +
                "radial-gradient(900px 500px at 85% 20%, rgba(99,102,241,.18), transparent 55%)," +
                "linear-gradient(180deg, #0B1220 0%, #070B14 100%)",
        },
        shell: {
            width: "100%",
            maxWidth: "980px",
            display: "grid",
            gridTemplateColumns: "1.1fr .9fr",
            gap: "20px",
            alignItems: "stretch",
        },
        leftPanel: {
            borderRadius: "20px",
            padding: "28px",
            background: "linear-gradient(135deg, rgba(255,255,255,.10), rgba(255,255,255,.04))",
            border: "1px solid rgba(255,255,255,.10)",
            backdropFilter: "blur(14px)",
            color: "rgba(255,255,255,.92)",
            boxShadow: "0 20px 60px rgba(0,0,0,.45)",
            position: "relative",
            overflow: "hidden",
        },
        rightCard: {
            borderRadius: "20px",
            padding: "28px",
            background: "rgba(255,255,255,.08)",
            border: "1px solid rgba(255,255,255,.12)",
            backdropFilter: "blur(16px)",
            boxShadow: "0 20px 60px rgba(0,0,0,.45)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
        },
        logoBadge: {
            width: "44px",
            height: "44px",
            borderRadius: "12px",
            background: "linear-gradient(135deg, rgba(59,130,246,.9), rgba(99,102,241,.9))",
            display: "grid",
            placeItems: "center",
            boxShadow: "0 12px 30px rgba(59,130,246,.25)",
            marginBottom: "14px",
        },
        title: {fontSize: "24px", fontWeight: 650, letterSpacing: "-0.02em", color: "rgba(255,255,255,.95)"},
        subtitle: {marginTop: "6px", fontSize: "13px", color: "rgba(255,255,255,.68)", lineHeight: 1.4},
        label: {fontSize: "12px", color: "rgba(255,255,255,.72)", margin: "10px 0 6px"},
        input: {
            width: "100%",
            borderRadius: "14px",
            padding: "12px 12px 12px 40px",
            background: "rgba(255,255,255,.10)",
            border: "1px solid rgba(255,255,255,.14)",
            color: "rgba(255,255,255,.95)",
            outline: "none",
        },
        inputRightIconPadding: {paddingRight: "44px"},
        hint: {fontSize: "12px", color: "rgba(255,255,255,.60)"},
        alert: {
            marginTop: "12px",
            padding: "10px 12px",
            borderRadius: "12px",
            background: "rgba(239,68,68,.12)",
            border: "1px solid rgba(239,68,68,.30)",
            color: "rgba(255,255,255,.90)",
            fontSize: "13px",
        },
        lockout: {
            marginTop: "12px",
            padding: "10px 12px",
            borderRadius: "12px",
            background: "rgba(245,158,11,.12)",
            border: "1px solid rgba(245,158,11,.28)",
            color: "rgba(255,255,255,.90)",
            fontSize: "13px",
        },
        primaryButton: {
            width: "100%",
            height: "44px",
            borderRadius: "14px",
            fontWeight: 650,
            background: "linear-gradient(135deg, #3B82F6, #6366F1)",
            border: "none",
        },
        secondaryLine: {marginTop: "12px", fontSize: "12px", color: "rgba(255,255,255,.60)", textAlign: "center"},
    };

    // Responsive fallback when the screen is narrow
    const isNarrow = typeof window !== "undefined" && window.innerWidth < 900;

    return (
        <>
            <ToastContainer autoClose={1000} position="top-right"/>
            <div style={styles.page}>
                <div
                    style={{
                        ...styles.shell,
                        gridTemplateColumns: isNarrow ? "1fr" : styles.shell.gridTemplateColumns,
                        maxWidth: isNarrow ? "520px" : styles.shell.maxWidth,
                    }}
                >
                    {!isNarrow && (
                        <section style={styles.leftPanel}>
                            <div style={{display: "flex", alignItems: "center", gap: "12px", marginBottom: "14px"}}>
                                <div style={styles.logoBadge}>
                                    <span style={{fontWeight: 800, color: "white"}}>M</span>
                                </div>

                                <div style={{display: "flex", flexDirection: "column", lineHeight: 1.1}}>
                                    <div style={{fontSize: "18px", fontWeight: 800, letterSpacing: "-0.02em"}}>
                                        Munshi
                                    </div>
                                    <div style={{fontSize: "12px", color: "rgba(255,255,255,.65)"}}>
                                        Secure Portal
                                    </div>
                                </div>
                            </div>

                            <div style={{fontSize: "28px", fontWeight: 750, letterSpacing: "-0.03em"}}>
                                Welcome back
                            </div>
                            <div style={{marginTop: "10px", color: "rgba(255,255,255,.72)", lineHeight: 1.6}}>
                                Sign in to access your workspace, reports, and secure data tools.
                            </div>

                            <div style={{marginTop: "18px", display: "grid", gap: "10px"}}>
                                <div style={{padding: "12px 14px", borderRadius: "14px", border: "1px solid rgba(255,255,255,.10)", background: "rgba(255,255,255,.06)"}}>
                                    <div style={{fontSize: "12px", color: "rgba(255,255,255,.65)"}}>Tip</div>
                                    <div style={{marginTop: "4px", fontSize: "13px", color: "rgba(255,255,255,.85)"}}>
                                        Choose the correct database before signing in.
                                    </div>
                                </div>
                                <div style={{padding: "12px 14px", borderRadius: "14px", border: "1px solid rgba(255,255,255,.10)", background: "rgba(255,255,255,.06)"}}>
                                    <div style={{fontSize: "12px", color: "rgba(255,255,255,.65)"}}>Security</div>
                                    <div style={{marginTop: "4px", fontSize: "13px", color: "rgba(255,255,255,.85)"}}>
                                        Multiple failed attempts will temporarily lock the account.
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    <main style={styles.rightCard}>
                        <div style={{display: "flex", flexDirection: "column", gap: "6px"}}>
                            <div style={{display: "flex", alignItems: "center", gap: "10px", marginBottom: "2px"}}>
                                <div
                                    style={{
                                        width: "26px",
                                        height: "26px",
                                        borderRadius: "10px",
                                        background: "linear-gradient(135deg, rgba(59,130,246,.9), rgba(99,102,241,.9))",
                                        display: "grid",
                                        placeItems: "center",
                                        boxShadow: "0 10px 24px rgba(59,130,246,.22)",
                                    }}
                                >
                                    <span style={{fontWeight: 900, color: "white", fontSize: "13px"}}>M</span>
                                </div>

                                <div style={{fontSize: "13px", fontWeight: 800, letterSpacing: ".08em", color: "rgba(255,255,255,.78)"}}>
                                    MUNSHI
                                </div>
                            </div>

                            <div style={styles.title}>Sign in</div>
                            <div style={styles.subtitle}>Enter your credentials to continue.</div>
                        </div>

                        <form onSubmit={handleSubmit} style={{marginTop: "16px"}}>
                            <div style={styles.label}>Username</div>
                            <IconField style={{width: "100%"}}>
                                <InputIcon className="pi pi-user" style={{color: "rgba(255,255,255,.70)"}}/>
                                <InputText
                                    type="text"
                                    value={user}
                                    onChange={(e) => setUser(e.target.value)}
                                    placeholder="e.g. john.doe"
                                    className="w-full"
                                    style={styles.input}
                                    autoComplete="username"
                                />
                            </IconField>

                            <div style={styles.label}>Password</div>
                            <div style={{position: "relative"}}>
                                <IconField style={{width: "100%"}}>
                                    <InputIcon className="pi pi-lock" style={{color: "rgba(255,255,255,.70)"}}/>
                                    <InputText
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Your password"
                                        className="w-full"
                                        style={{...styles.input, ...styles.inputRightIconPadding}}
                                        autoComplete="current-password"
                                    />
                                </IconField>

                                <button
                                    type="button"
                                    onClick={() => setShowPassword(v => !v)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        width: "34px",
                                        height: "34px",
                                        borderRadius: "12px",
                                        border: "1px solid rgba(255,255,255,.14)",
                                        background: "rgba(255,255,255,.08)",
                                        color: "rgba(255,255,255,.85)",
                                        cursor: "pointer",
                                    }}
                                >
                                    <i className={showPassword ? "pi pi-eye-slash" : "pi pi-eye"} />
                                </button>
                            </div>

                            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "12px"}}>
                                <div style={styles.hint}>
                                    Attempts: {Math.min(failedAttempts, MAX_ATTEMPTS)}/{MAX_ATTEMPTS}
                                </div>
                                <div style={styles.hint}>
                                    {dbName ? `DB: ${dbName}` : "Select a database"}
                                </div>
                            </div>

                            <div style={{marginTop: "10px"}}>
                                <DropSelect
                                    disable={false}
                                    selectedItem={dbName}
                                    setItem={handleDatabaseChange}
                                    selectList={databases}
                                />
                            </div>

                            {isLockedOut && (
                                <div style={styles.lockout}>
                                    Account locked. Try again in <b>{lockoutTimer}</b> seconds.
                                </div>
                            )}

                            {loginError && !isLockedOut && (
                                <div style={styles.alert}>
                                    Invalid username or password. Please try again.
                                </div>
                            )}

                            <div style={{marginTop: "14px"}}>
                                <Button
                                    label={loading ? "Signing in..." : "Sign In"}
                                    className="w-full"
                                    style={styles.primaryButton}
                                    onClick={handleSubmit}
                                    disabled={isLockedOut || loading}
                                />
                            </div>

                            <div style={styles.secondaryLine}>
                                Protected access • Corporate SSO-ready styling (even if the backend isn’t—yet)
                            </div>
                        </form>
                    </main>
                </div>
            </div>
        </>
    );
}
